import { createActorContext } from '@xstate/react';
import React from 'react';
import { genericUploadQueueMachine } from './machine';

const GenericUploadQueueContext = createActorContext(genericUploadQueueMachine);

const beforeUnloadHandler = (e: Event) => {
    e.preventDefault();
    return null;
};

function GenericUploadQueueProvider({
    children,
    uploadDestination,
    onDocumentUploaded,
    onDocumentCleared,
    onUploadFailed,
    onIsUploadingChanged,
    onFailedUploadCleared,
    defaultFiles = [],
}: {
    uploadDestination: string;
    children: React.ReactNode;
    onIsUploadingChanged?: (isUploading: boolean) => void;
    onDocumentUploaded: (document: {
        fileReference: string;
        name: string;
        size: number;
    }) => void;
    onUploadFailed?: (document: {
        fileReference: string;
        name: string;
        size: number;
    }) => void;
    onFailedUploadCleared?: (fileReference: string) => void;
    onDocumentCleared: (fileReference: string) => void;
    defaultFiles: {
        fileReference: string;
        name: string;
        size: number;
    }[];
}) {
    return (
        <GenericUploadQueueContext.Provider
            logic={genericUploadQueueMachine.provide({
                actions: {
                    uploadedCompleted: (_, {
                        uploadFileReference,
                        uploadFileName,
                        uploadFileSize,
                    }) => {
                        onDocumentUploaded({
                            fileReference: uploadFileReference,
                            name: uploadFileName,
                            size: uploadFileSize,
                        });
                    },
                    uploadFailed: (_, {
                        uploadFileReference,
                        uploadFileName,
                        uploadFileSize,
                    }) => {
                        onUploadFailed?.({
                            fileReference: uploadFileReference,
                            name: uploadFileName,
                            size: uploadFileSize,
                        });
                    },
                    queueLengthChanged: (_, { activeCount }) => {
                        if (activeCount !== 0) {
                            window.addEventListener('beforeunload', beforeUnloadHandler);
                            onIsUploadingChanged?.(true);
                        } else {
                            window.removeEventListener('beforeunload', beforeUnloadHandler);
                            onIsUploadingChanged?.(false);
                        }
                    },
                    uploadCleared: (_, { uploadedFileReference, id }) => {
                        if (uploadedFileReference === null) {
                            onFailedUploadCleared?.(id);
                            return;
                        }
                        onDocumentCleared(uploadedFileReference);
                    },
                },
            })}
            options={{
                input: {
                    uploadCapacity: 2, // Allow 2 files to be uploaded at once
                    maxFileSizeBytes: 1024 * 1024 * 200, // 200mb
                    uploadDestination,
                    defaultFiles: defaultFiles.map((x) => ({
                        uploadedFileReference: x.fileReference,
                        fileName: x.name,
                    })),
                },
            }}
        >
            { children}
        </GenericUploadQueueContext.Provider>
    );
}

export { GenericUploadQueueProvider, GenericUploadQueueContext };
